// src/components/donors/AddDonationModal.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FiX, FiDollarSign, FiCalendar, FiCreditCard, FiFolder, FiFileText, FiSave } from "react-icons/fi";

/**
 * A modal form for adding a new donation record to an existing donor.
 * It fetches available projects and the donor's name to provide a better user experience.
 * @param {string} donorId - The ID of the donor to add the donation to.
 * @param {Function} closeModal - Function to close the modal.
 * @param {Function} onDonationAdded - Callback to refresh data after a donation is added.
 */
export default function AddDonationModal({ donorId, closeModal, onDonationAdded }) {
    const modalRef = useRef(null);
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0], // Default to today
        amount: "", // Store amount as a string to handle formatting
        paymentMethod: "bank_transfer",
        project: "", // This will hold the project's ObjectId
        reference: "",
        notes: ""
    });

    const [donorName, setDonorName] = useState("");
    const [projects, setProjects] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [selectedProject, setSelectedProject] = useState(null);

    const API_URL = "https://ims.holdthechild.net";

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [closeModal]);

    /**
     * Fetches the donor's name and all available projects to populate the form.
     */
    const fetchData = useCallback(async () => {
        setFetching(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            // Fetch donor details to display their name in the modal header
            const donorResponse = await axios.get(`${API_URL}/api/donors/${donorId}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            setDonorName(donorResponse.data.data.displayName);

            // Fetch all projects for the dropdown
            const projectsResponse = await axios.get(`${API_URL}/api/projects`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            setProjects(projectsResponse.data.data);

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch necessary data.";
            setError(errorMessage);
        } finally {
            setFetching(false);
        }
    }, [donorId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- UPDATED handleChange FUNCTION ---
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Special handling for the 'amount' field
        if (name === "amount") {
            // Remove all non-digit characters to get a clean number string
            const rawValue = value.replace(/[^0-9]/g, '');

            // Ensure the value is a positive integer
            const numericValue = parseInt(rawValue, 10);

            if (isNaN(numericValue) || numericValue <= 0) {
                // If it's not a valid number or is zero/negative, clear the field or set to an empty string
                setFormData((prev) => ({ ...prev, [name]: "" }));
            } else {
                // Store the numeric value in a hidden field if you need it,
                // but for this example, we'll store the raw numeric string
                // and format it for display.
                const formattedValue = new Intl.NumberFormat('en-US').format(numericValue);
                setFormData((prev) => ({ ...prev, [name]: formattedValue }));
            }
        } else {
            // For all other fields, update the state as usual
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

            // If Project is changed, find the project object to show its details
            if (name === 'project') {
                const foundProject = projects.find(p => p._id === value);
                setSelectedProject(foundProject || null);
            }        
    };
    // --- END UPDATED handleChange FUNCTION ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // --- UPDATED SUBMISSION LOGIC ---
        // Create a temporary object to hold the data for the API call
        const donationData = { ...formData };

        // Remove commas from the amount string and convert it to an integer
        const cleanAmount = donationData.amount.replace(/,/g, '');
        donationData.amount = parseInt(cleanAmount, 10);

        if (!donationData.amount || !formData.paymentMethod) {
            setError("Amount and Payment Method are required.");
            return;
        }
        // --- END UPDATED SUBMISSION LOGIC ---

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            // Use the cleaned donationData object for the API call
            await axios.post(`${API_URL}/api/donors/${donorId}/donations`, donationData, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            setSuccess("Donation added successfully! 🎉");
            setTimeout(() => {
                onDonationAdded(); // This will close the modal and refresh the view.
            }, 1500);

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out flex flex-col items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mb-4"></div>
                    <p className="text-gray-700 font-medium">Loading projects and donor details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            {/* Popup Notifications */}
            {error && <div className="fixed top-5 z-[60] bg-red-500 text-white px-6 py-3 rounded shadow-lg animate-fade-in-out">{`❌ ${error}`}</div>}
            {success && <div className="fixed top-5 z-[60] bg-green-500 text-white px-6 py-3 rounded shadow-lg animate-fade-in-out">{`✅ ${success}`}</div>}

            <div 
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out flex flex-col max-h-[90vh]"
            >
                {/* Fixed Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <FiDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    Record Donation
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <FiFolder className="w-3 h-3 mr-1" />
                                    <span>Programs Department</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={closeModal} 
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <FiX size={20} />
                        </button>
                    </div>
                </div>

                {/* Scrollable Form Body */}
                <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                    <div className="mb-4">
                        <p className="text-sm text-gray-600">
                            Donor: <span className="font-medium text-gray-900">{donorName}</span>
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                        {/* Donation Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Donation Date <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiCalendar className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    name="date"
                                    type="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    required
                                />
                            </div>
                        </div>

                        {/* Amount */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Amount (USD) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiDollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    name="amount"
                                    type="text" // Change type to 'text' to handle commas
                                    value={formData.amount}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    placeholder="e.g., 500,000"
                                    required
                                />
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Method <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiCreditCard className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    name="paymentMethod"
                                    value={formData.paymentMethod}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                    required
                                >
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="credit_card">Credit Card</option>
                                    <option value="mobile_money">Mobile Money</option>
                                    <option value="cash">Cash</option>
                                    <option value="other">Other</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Project */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Project <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiFolder className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    name="project"
                                    value={formData.project}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                    required
                                >
                                    <option value="">Select a project...</option>
                                    {projects.map(proj => (
                                        <option key={proj._id} value={proj._id}>{proj.projectTitle}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>

                        {/* ✨ NEW: Display Budget Code when project is selected */}
                        {selectedProject && selectedProject.budgetCode && (
                            <div className="mt-2 p-2 bg-orange-50 rounded-md border border-orange-200 flex items-center justify-between">
                                <span className="text-xs text-orange-800 font-medium">Funds will be tagged to Budget Code:</span>
                                <span className="text-xs font-bold text-orange-700 bg-white px-2 py-1 rounded border border-orange-300 shadow-sm">
                                    {selectedProject.budgetCode}
                                </span>
                            </div>
                        )}

                        </div>

                        {/* Transaction Reference */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Transaction Reference (Optional)
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiFileText className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    name="reference"
                                    value={formData.reference}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    placeholder="e.g., Bank transaction ID"
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                    <FiFileText className="h-5 w-5 text-gray-400" />
                                </div>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows={3}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                                    placeholder="Additional notes about this donation..."
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                            <button
                                type="button"
                                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200 order-2 sm:order-1"
                                onClick={closeModal}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 order-1 sm:order-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="w-4 h-4" />
                                        <span>Record Donation</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}